import { useTranslation } from "react-i18next";
import React from "react";
import { Typography } from "antd";
import Table from "../../../components/Common/dataTable/index";
import FeesStatusBadge from "../../../components/Common/Badge/FeesStatusBadge";
import { all_routes } from "../../../router/all_routes";
import ActionsDropdown, { Action } from "../../../components/Common/ActionsDropdown";
import { formatMoney } from "../../../helpers/numberHelper";
import { StudentType } from "types/studentTypes";
import UserProfileHeader from "components/Common/ui/UserProfileHeader";
import { formatToLocaleDate } from "utils/dateUtils";
import { useAbility } from "config/abilityContext";
import { showConfirmDialog } from "utils/confirmDialogUtils";
import { ExclamationCircleOutlined } from "@ant-design/icons";

const { Text } = Typography;

interface Props {
  dataSource?: any[];
  onEdit?: (record: any) => void;
  onDelete?: (id: string) => void;
  loading?: boolean;
  hideActions?: boolean;
  dataPaginate?: any;
  onChange?: (pg: any) => void;
}

const StudentFeesTable: React.FC<Props> = ({ dataSource = [], onEdit, onDelete, loading, hideActions = false, dataPaginate,
  onChange, }) => {
  const { t } = useTranslation();
  const ability = useAbility();

  const showDeleteConfirm = (id: string) => {
    showConfirmDialog({
      title: t("are_you_sure_you_want_to_delete_this_fee"),
      icon: <ExclamationCircleOutlined />,
      content: t("this_action_cannot_be_undone"),
      okText: t("delete"),
      okType: "danger",
      cancelText: t("cancel"),
      loading,
      onOk: () => onDelete && onDelete(id),
    });
  };



  const columns = [
    {
      title: t("profile"),
      dataIndex: "student", // for sorting
      render: (_: any, record: any) => (
        <UserProfileHeader
          photo={record.student.user?.photo}
          firstName={record.student.user?.firstName}
          lastName={record.student.user?.lastName}
          subtitle={record.student.massarCode}
          to={all_routes.studentDetails.replace(":id", record?.student?.id || "") + all_routes.studentDetailsTabs.fees}
          canRedirect={ability.can("getAll", "Fee")} />
      ),
      width: "20%",
    },
    {
      title: t("fee_type"),
      dataIndex: ["feeType", "name"],
      key: "feeType",
      width: "15%"
    },
    {
      title: t("amount"),
      dataIndex: "amount",
      key: "amount",
      width: "15%",
      render: (amount: number) => formatMoney(amount)
    },

    {
      title: t("due_date"),
      dataIndex: "dueDate",
      key: "dueDate",
      width: "15%",
      render: (dueDate: string | undefined) => formatToLocaleDate(dueDate),
    },
    {
      title: t("status"),
      dataIndex: "computedStatus",
      key: "computedStatus",
      width: "15%",
      render: (computedStatus: "pending" | "paid" | "overdue" | "coming" | "failed" | "partially_paid", record: any) => (
        <FeesStatusBadge
          status={computedStatus}
        />
      )
    },
    {
      title: t("action"),
      dataIndex: "action",
      hidden: hideActions,
      render: (_: string, record: any) => {
        const actions: Action[] = [
          {
            label: t("student_details"),
            icon: "ti ti-eye",
            actionType: "link",
            to: `${all_routes.studentDetails.replace(":id", record.studentId)}${all_routes.studentDetailsTabs.fees}`,
          },
          {
            label: t("delete"),
            icon: "ti ti-trash", 
            actionType: "link",
            onClick: () => onDelete && showDeleteConfirm(record.id),
            hidden: record.status !== "pending" || !ability.can("delete", "Fee")
          },

        ];
        return <ActionsDropdown actions={actions} />;
      },
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={dataSource}
      pagination={true}
      dataPaginate={dataPaginate}
      onChange={onChange}
    />
  );
};

export default StudentFeesTable;
